/**
 * Context para gestión de configuración de moneda y formato.
 * Proporciona configuración financiera por sucursal y funciones de formateo consistentes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/endpoints";

// Configuración por defecto (Chile)
const DEFAULT_CONFIG = {
  currency_symbol: "$",
  decimal_places: 0,
  thousand_separator: ".",
  decimal_separator: ",",
  default_tax_rate: 19.00,
  show_tax_breakdown: true,
};

const CurrencyConfigContext = createContext({
  config: DEFAULT_CONFIG,
  formatCurrency: (value) => `$ ${value}`,
  parseCurrency: (string) => 0,
  loading: true,
  error: null,
  refreshConfig: () => {},
});

/**
 * Provider para configuración de moneda.
 * Carga automáticamente la configuración de la sucursal activa del usuario.
 */
export const CurrencyConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);

  /**
   * Función para formatear moneda según la configuración.
   */
  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined || value === '') {
      return `${config.currency_symbol} 0`;
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    
    if (isNaN(numValue)) {
      return `${config.currency_symbol} 0`;
    }

    // Formatear con los decimales especificados
    let formatted;
    if (config.decimal_places > 0) {
      formatted = numValue.toFixed(config.decimal_places);
    } else {
      formatted = Math.round(numValue).toString();
    }

    // Separar parte entera y decimal
    const parts = formatted.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';

    // Aplicar separador de miles
    if (config.thousand_separator && integerPart.length > 3) {
      // Manejar números negativos
      const isNegative = integerPart.startsWith('-');
      if (isNegative) {
        integerPart = integerPart.substring(1);
      }
      
      // Agrupar de a 3 desde la derecha
      const reversed = integerPart.split('').reverse().join('');
      const grouped = reversed.match(/.{1,3}/g) || [];
      integerPart = grouped.join(config.thousand_separator).split('').reverse().join('');
      
      if (isNegative) {
        integerPart = '-' + integerPart;
      }
    }

    // Unir partes
    if (decimalPart && config.decimal_places > 0) {
      return `${config.currency_symbol} ${integerPart}${config.decimal_separator}${decimalPart}`;
    }
    
    return `${config.currency_symbol} ${integerPart}`;
  }, [config]);

  /**
   * Función para parsear string de moneda a número.
   */
  const parseCurrency = useCallback((string) => {
    if (!string) return 0;
    
    // Remover símbolo de moneda y espacios
    let cleaned = string.replace(config.currency_symbol, '').trim();
    
    // Remover separadores de miles
    if (config.thousand_separator) {
      cleaned = cleaned.split(config.thousand_separator).join('');
    }
    
    // Reemplazar separador decimal por punto
    if (config.decimal_separator !== '.') {
      cleaned = cleaned.replace(config.decimal_separator, '.');
    }
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }, [config]);

  /**
   * Cargar configuración financiera de la sucursal.
   */
  const loadConfig = useCallback(async (branchId) => {
    // Asegurar que branchId sea un string/number, no un objeto
    const id = typeof branchId === 'object' ? branchId?.id || branchId?.value : branchId;
    
    if (!id) {
      setConfig(DEFAULT_CONFIG);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Endpoint: GET /api/finance/branch-configs/by_branch/{branch_id}/
      const response = await api.finance.branchConfigs.getByBranch(id);
      
      if (response && response.data) {
        setConfig({
          currency_symbol: response.data.currency_symbol || DEFAULT_CONFIG.currency_symbol,
          decimal_places: response.data.decimal_places ?? DEFAULT_CONFIG.decimal_places,
          thousand_separator: response.data.thousand_separator ?? DEFAULT_CONFIG.thousand_separator,
          decimal_separator: response.data.decimal_separator || DEFAULT_CONFIG.decimal_separator,
          default_tax_rate: parseFloat(response.data.default_tax_rate) || DEFAULT_CONFIG.default_tax_rate,
          show_tax_breakdown: response.data.show_tax_breakdown ?? DEFAULT_CONFIG.show_tax_breakdown,
        });
      } else {
        setConfig(DEFAULT_CONFIG);
      }
    } catch (err) {
      console.error("[CurrencyConfig] Error loading config:", err);
      setError(err.message || "Error al cargar configuración");
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refrescar configuración actual.
   */
  const refreshConfig = useCallback(() => {
    if (currentBranch) {
      loadConfig(currentBranch);
    }
  }, [currentBranch, loadConfig]);

  /**
   * Obtener sucursal activa del usuario.
   */
  useEffect(() => {
    const getBranchFromStorage = () => {
      // Intentar obtener de localStorage
      const storedBranch = localStorage.getItem('selectedBranch');
      if (storedBranch) {
        try {
          const branch = JSON.parse(storedBranch);
          return branch.id || branch.value;
        } catch (e) {
          return storedBranch;
        }
      }
      
      // Intentar obtener sucursal por defecto del usuario
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const defaultBranch = user.branch_assignments?.find(b => b.is_default);
          if (defaultBranch) {
            return defaultBranch.branch_id;
          }
          // Si no hay sucursal por defecto, usar la primera asignada
          if (user.branch_assignments && user.branch_assignments.length > 0) {
            return user.branch_assignments[0].branch_id;
          }
        } catch (e) {
          console.error("[CurrencyConfig] Error parsing user:", e);
        }
      }
      
      return null;
    };

    const branchId = getBranchFromStorage();
    setCurrentBranch(branchId);
    
    if (branchId) {
      loadConfig(branchId);
    } else {
      setConfig(DEFAULT_CONFIG);
      setLoading(false);
    }
  }, [loadConfig]);

  /**
   * Escuchar cambios en la sucursal seleccionada.
   */
  useEffect(() => {
    const handleBranchChange = (event) => {
      const newBranchId = event.detail;
      setCurrentBranch(newBranchId);
      loadConfig(newBranchId);
    };

    window.addEventListener('branchChanged', handleBranchChange);
    
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
    };
  }, [loadConfig]);

  const value = {
    config,
    formatCurrency,
    parseCurrency,
    loading,
    error,
    refreshConfig,
  };

  return (
    <CurrencyConfigContext.Provider value={value}>
      {children}
    </CurrencyConfigContext.Provider>
  );
};

/**
 * Hook para usar configuración de moneda.
 */
export const useCurrencyConfig = () => {
  const context = useContext(CurrencyConfigContext);
  if (!context) {
    throw new Error('useCurrencyConfig must be used within a CurrencyConfigProvider');
  }
  return context;
};

export default CurrencyConfigContext;

